import { Box } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { MemoEmptyChart } from 'charts/common/EmptyChart';
import type { BuildChartContext, ChartData } from '../catalog/types';
import {
  buildDonutLegendItems,
  formatDonutSliceTooltipValue,
  getDonutTotalValue,
} from '../formatters/buildDonutLegendItems';
import {
  CHART_DONUT_DASHBOARD_PIE_MAX_SIZE,
  CHART_DONUT_DETAILS_PIE_MAX_SIZE,
  CHART_DONUT_INNER_RADIUS,
  getChartDonutMargin,
} from '../tokens/muiChartLayout';
import type { ReactElement } from 'react';
import type { IntlShape } from 'react-intl';
import { buildDonutPieData } from '../formatters/buildDonutPieData';
import { ChartSvgDefs } from './ChartSvgDefs';
import { DonutChartCenterTotal } from './DonutChartCenterTotal';
import { DonutChartLegend } from './DonutChartLegend';
import {
  CHART_DONUT_STYLE,
  getChartAreaSx,
  getDashboardChartAreaSx,
  getChartTooltipSx,
} from '../theme/muiChartSlotProps';

type DonutChartViewProps = Readonly<{
  chartId: string;
  data: ChartData;
  height: number;
  colors: readonly string[];
  intl: IntlShape;
  variant: BuildChartContext['variant'];
}>;

export function DonutChartView({
  chartId,
  data,
  height,
  colors,
  intl,
  variant,
}: DonutChartViewProps): ReactElement {
  if (data.values.length === 0) {
    return <MemoEmptyChart variant={variant} />;
  }

  const pieData = buildDonutPieData(data, chartId);
  const total = getDonutTotalValue(data);
  const legendItems = buildDonutLegendItems(data, colors, intl);
  const pieSize =
    variant === 'dashboard'
      ? Math.min(height, CHART_DONUT_DASHBOARD_PIE_MAX_SIZE)
      : Math.min(height, CHART_DONUT_DETAILS_PIE_MAX_SIZE);

  const pieChart = (
    <Box
      sx={{
        position: 'relative',
        flex: `0 0 ${pieSize}px`,
        width: pieSize,
        height: pieSize,
        overflow: 'visible',
      }}
    >
      <ChartSvgDefs chartId={chartId} colors={colors} />
      <PieChart
        series={[
          {
            data: pieData,
            innerRadius: CHART_DONUT_INNER_RADIUS,
            paddingAngle:
              variant === 'dashboard'
                ? CHART_DONUT_STYLE.dashboardPaddingAngle
                : CHART_DONUT_STYLE.paddingAngle,
            cornerRadius:
              variant === 'dashboard'
                ? CHART_DONUT_STYLE.dashboardCornerRadius
                : CHART_DONUT_STYLE.cornerRadius,
            arcLabelMinAngle: CHART_DONUT_STYLE.arcLabelMinAngle,
            valueFormatter: (item) =>
              formatDonutSliceTooltipValue(intl, item.value, total),
            highlightScope: { highlight: 'item', fade: 'global' },
            highlighted: {
              additionalRadius:
                variant === 'dashboard'
                  ? CHART_DONUT_STYLE.dashboardHighlightAdditionalRadius
                  : CHART_DONUT_STYLE.highlightAdditionalRadius,
            },
          },
        ]}
        width={pieSize}
        height={pieSize}
        margin={getChartDonutMargin(variant)}
        hideLegend
        slotProps={{ tooltip: { sx: getChartTooltipSx() } }}
        sx={{ overflow: 'visible' }}
      />
      <DonutChartCenterTotal total={total} />
    </Box>
  );

  if (variant === 'dashboard') {
    return (
      <Box
        sx={{
          display: 'flex',
          flex: '0 0 auto',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          width: 'max-content',
          maxWidth: '100%',
          overflow: 'visible',
        }}
      >
        {pieChart}
        <DonutChartLegend items={legendItems} layout={variant} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...getChartAreaSx(),
        height,
        minHeight: height,
        width: '100%',
        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: 'max-content',
            maxWidth: '100%',
            overflow: 'visible',
          }}
        >
          {pieChart}
          <DonutChartLegend items={legendItems} layout={variant} />
        </Box>
      </Box>
    </Box>
  );
}
